import { Button, Form, Input, type InputRef } from 'antd';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  /** Prefill (without extension). Defaults to "New note" for create. */
  initialName?: string;
  /** Called with the note name (without extension) when the form is submitted. */
  onSubmit: (name: string) => void;
  onCancel: () => void;
}

/**
 * NoteFileForm
 * Name a note before create (`.fgnote`) or rename an existing one.
 */
export const NoteFileForm = ({ initialName, onSubmit, onCancel }: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const inputRef = useRef<InputRef>(null);

  const handleFinish = (values: { fileName: string }) => {
    onSubmit(values.fileName.trim());
  };

  // Prefill + autofocus on mount / locale / rename target change
  useEffect(() => {
    form.setFieldsValue({ fileName: initialName ?? t('note.newNote') });
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 200);
  }, [form, initialName, t]);

  return (
    <Form layout="vertical" form={form} size="large" onFinish={handleFinish}>
      <Form.Item
        name="fileName"
        label={t('note.fileName')}
        className="mb-2!"
        normalize={(v: string) => v.replace(/\s{3,}/g, ' ').trimStart()}
        rules={[
          {
            required: true,
            message: t('note.fileNameRequired'),
            transform: (value: string) => value?.trim(),
          },
        ]}
      >
        <Input
          ref={inputRef}
          autoFocus
          autoCapitalize="none"
          placeholder={t('note.fileName')}
          maxLength={255}
        />
      </Form.Item>

      <div className="mt-2 flex flex-row items-center justify-end gap-2">
        <Button size="middle" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button size="middle" type="primary" htmlType="submit">
          {t('common.save')}
        </Button>
      </div>
    </Form>
  );
};

export default NoteFileForm;
